/**
 * Default styling for the SDK's rendered widgets (the imperative
 * `render*` widgets and the React components, which share these classnames).
 * Framework-free so the core bundle can inject it without pulling in React.
 *
 * Every selector is wrapped in `:where()` so it has zero specificity — any
 * rule in the host app's CSS (even a bare class selector) overrides it. The
 * `--stoq-*` custom properties are the quick knobs; set them on `:root` (or
 * any ancestor) to retheme without writing component CSS.
 */

export const STOQ_COMPONENT_STYLES = `
:where(.stoq-preorder-badge) {
  display: inline-block;
  padding: 2px 8px;
  border-radius: var(--stoq-radius, 4px);
  background: var(--stoq-badge-background, #eef1f4);
  color: var(--stoq-badge-color, #303030);
  font-size: 0.8em;
  line-height: 1.6;
}
:where(.stoq-preorder-button),
:where(.stoq-notify-me__button),
:where(.stoq-notify-me__submit) {
  display: inline-block;
  padding: 10px 18px;
  border: none;
  border-radius: var(--stoq-radius, 4px);
  background: var(--stoq-accent, #1a1a1a);
  color: var(--stoq-accent-contrast, #ffffff);
  font: inherit;
  cursor: pointer;
}
:where(.stoq-preorder-button:disabled),
:where(.stoq-notify-me__submit:disabled) {
  opacity: 0.6;
  cursor: default;
}
:where(.stoq-notify-me__form) {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
:where(.stoq-notify-me__input) {
  padding: 9px 12px;
  border: 1px solid var(--stoq-border, #c9cccf);
  border-radius: var(--stoq-radius, 4px);
  font: inherit;
  min-width: 220px;
}
:where(.stoq-notify-me__error) {
  flex-basis: 100%;
  margin: 0;
  color: var(--stoq-error, #b3261e);
  font-size: 0.9em;
}
:where(.stoq-notify-me__success) {
  margin: 0;
  color: var(--stoq-success, #1a7f37);
}
`

const STYLE_ATTRIBUTE = 'data-stoq-sdk-components'

/**
 * Injects the default component stylesheet once per document (browser-only;
 * caller guards SSR). Skipped entirely when a `[data-stoq-sdk-components]`
 * style or link element already exists, so hosts can pre-empt the defaults by
 * shipping their own.
 */
export function injectComponentStyles(doc: Document): void {
  if (doc.querySelector(`[${STYLE_ATTRIBUTE}]`) !== null) return
  const style = doc.createElement('style')
  style.setAttribute(STYLE_ATTRIBUTE, '')
  style.textContent = STOQ_COMPONENT_STYLES
  doc.head.appendChild(style)
}
